'use client';

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { cn } from '@core/helpers/misc/GeneralUtils';
import { useProfileOrganizations } from '@core/ui/profile/ProfileOrganizationProvider';
import { useNavScrollOptional } from '@/contexts/NavScrollContext';
import { useHeadings } from '@/contexts/HeadingsContext';
import { usePanelOptional } from '@/core/helpers/react/contexts/PanelContext';
import { NAV_WIDE_BREAKPOINT } from '@core/helpers/styles/layoutBreakpoints';
import { NavMenuSection } from './NavMenuSection';
import { dispatchVaultsUpdate } from '@vaults/core/VaultNavContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@core/ui/primitives/dropdown-menu';
import {
  LuArrowLeft,
  LuArrowRight,
  LuRefreshCw,
  LuPenLine,
  LuUndo2,
  LuRedo2,
  LuSave,
  LuSend,
  LuX,
  LuCopy,
  LuDownload,
  LuFileText,
  LuList,
  LuMessageSquare,
  LuEye,
  LuCircleCheck,
  LuChevronDown,
  LuBot,
  LuCheck,
  LuLoader,
} from 'react-icons/lu';
import type { DocumentDraftStatus } from '@/contexts/NavScrollContext';
import { ActionBarTooltip } from '@core/ui/primitives/action-bar-tooltip';

// Platform organization slug - anchors are only shown for this org
const PLATFORM_ORG_SLUG = 'inovisum';

// Page sections shown for the platform organization (order matches page layout)
const PAGE_SECTIONS = [
  { label: 'Welcome', anchor: 'welcome' },
];

// Showcase items with dropdown submenu
const SHOWCASE_ITEMS = [
  { label: 'Diagrams', href: '/showcase/diagrams' },
  { label: 'Mindmaps', href: '/showcase/mindmap' },
  { label: 'Color Selector', href: '/showcase/color-selector-demo' },
  { label: 'Themer', href: '/showcase/themer' },
];

// Routes where we use hash anchors directly (same page scroll)
const LANDING_ROUTES = ['/welcome', '/'];

// Routes that should show platform org menu items (Welcome, Showcase)
// These are generic/utility pages not belonging to any specific organization
const PLATFORM_MENU_ROUTES = [
  '/no-access',
  '/login-required',
  '/v/',
  '/auth',
  '/contact',
  '/search',
  '/search-results',
  '/showcase',
  '/vaults',
  '/register',
];

// Theme-aware styles for action bar buttons
const styles = {
  group: 'inline-flex rounded-lg border overflow-hidden bg-gray-100 border-gray-200 dark:bg-white/5 dark:border-white/10',
  btn: 'h-8 w-8 flex items-center justify-center transition-colors text-gray-400 hover:text-gray-700 hover:bg-gray-200 dark:text-white/50 dark:hover:text-white dark:hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-400 dark:disabled:hover:text-white/50',
  btnActive: 'text-emerald-600 bg-emerald-50 dark:text-emerald-400 dark:bg-emerald-400/10',
  btnCopied: '!text-brand bg-brand/10 hover:bg-brand/10 dark:!text-brand dark:bg-brand/10 dark:hover:bg-brand/10',
  borderL: 'border-l border-gray-200 dark:border-white/10',
};

// Workflow action definitions
type WorkflowAction = 'draft' | 'mark_for_review' | 'validate' | 'publish';

interface WorkflowActionInfo {
  id: WorkflowAction;
  label: string;
  colorClass: string;
}

const workflowActions: WorkflowActionInfo[] = [
  { id: 'draft', label: 'Draft', colorClass: '!text-warning' },
  { id: 'mark_for_review', label: 'Review', colorClass: '!text-violet-500' },
  { id: 'validate', label: 'Validate', colorClass: '!text-brand' },
  { id: 'publish', label: 'Publish', colorClass: '!text-blue-500' },
];

/** Get icon component for workflow action */
function getWorkflowIcon(actionId: WorkflowAction, colorClass: string) {
  const iconProps = { className: cn('h-4 w-4', colorClass) };
  switch (actionId) {
    case 'draft':
      return <LuPenLine {...iconProps} />;
    case 'mark_for_review':
      return <LuEye {...iconProps} />;
    case 'validate':
      return <LuCircleCheck {...iconProps} />;
    case 'publish':
      return <LuSend {...iconProps} />;
  }
}

/** Returns the primary (next) action based on current status */
function getPrimaryAction(status?: DocumentDraftStatus): WorkflowAction {
  switch (status) {
    case 'marked_for_review':
      return 'validate';
    case 'validated':
      return 'publish';
    case 'new':
    case 'draft':
    default:
      return 'mark_for_review';
  }
}

/** Maps current status to corresponding action (for filtering) */
function statusToAction(status?: DocumentDraftStatus): WorkflowAction | null {
  switch (status) {
    case 'draft':
    case 'new':
      return 'draft';
    case 'marked_for_review':
      return 'mark_for_review';
    case 'validated':
      return 'validate';
    default:
      return null;
  }
}

/** Returns available actions (all except current state and primary) */
function getSecondaryActions(status?: DocumentDraftStatus): WorkflowAction[] {
  const primary = getPrimaryAction(status);
  const current = statusToAction(status);
  return workflowActions.map((a) => a.id).filter((a) => a !== primary && a !== current);
}

/**
 * NavCenterContent - Dual-view navigation content with swap animation
 *
 * VIEW 1 (normal): Navigation links (Features, How it works, etc.) + vault links
 * VIEW 2 (scrolled): Action bar when in vault and scrolled past header
 *
 * The two views swap with translate-y animations like layout2.
 */
export function NavCenterContent() {
  const pathname = usePathname() ?? '';
  const { currentOrganization, loading } = useProfileOrganizations();
  const navScroll = useNavScrollOptional();
  const panelContext = usePanelOptional();
  const [activeHash, setActiveHash] = useState('');
  const [mounted, setMounted] = useState(false);
  const [isBelowWide, setIsBelowWide] = useState(false);

  useEffect(() => {
    setMounted(true);
    setActiveHash(window.location.hash);

    const handleHashChange = () => setActiveHash(window.location.hash);
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);


  // Track viewport width to inject menu into sidebar when below wide breakpoint
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const checkWidth = () => {
      setIsBelowWide(window.innerWidth < NAV_WIDE_BREAKPOINT);
    };

    checkWidth();
    window.addEventListener('resize', checkWidth);
    return () => window.removeEventListener('resize', checkWidth);
  }, []);

  // Check if we're on a generic/platform route (not org-specific)
  const isPlatformRoute = PLATFORM_MENU_ROUTES.some((route) => pathname === route || pathname.startsWith(route));

  // Check if we're in the platform organization (either explicitly or via generic route)
  const isPlatformOrg = currentOrganization?.slug === PLATFORM_ORG_SLUG || isPlatformRoute;

  // Check if we're on a landing page (for hash-only anchors vs full URL)
  const isLandingPage = LANDING_ROUTES.some((route) => pathname === route || pathname.startsWith(route + '/'));

  // Get vaults from current organization (memoized to keep stable identity when org is not yet resolved)
  const vaults = useMemo(() => currentOrganization?.vaults ?? [], [currentOrganization?.vaults]);

  // Dispatch vault data to global store for breadcrumb navigation
  useEffect(() => {
    if (vaults.length > 0) {
      dispatchVaultsUpdate(
        vaults.map((vault) => ({
          slug: vault.slug,
          title: vault.title,
          href: vault.href,
        }))
      );
    }
  }, [vaults]);

  // Show anchors for platform org, landing page, or any platform route
  const showAnchors = isPlatformOrg || isLandingPage;

  // Build nav menu groups for sidebar injection
  const navMenuGroups = useMemo(() => {
    const groups: { title?: string; items: { label: string; href: string; isAnchor?: boolean }[] }[] = [];

    // Page sections (platform org only)
    if (showAnchors) {
      groups.push({
        items: PAGE_SECTIONS.map((section) => ({
          label: section.label,
          href: isLandingPage ? `#${section.anchor}` : `/welcome#${section.anchor}`,
          isAnchor: true,
        })),
      });
    }

    // Showcase items (only for platform organization owner)
    if (isPlatformOrg) {
      groups.push({
        title: 'Showcase',
        items: SHOWCASE_ITEMS.map((item) => ({
          label: item.label,
          href: item.href,
        })),
      });
    }

    // Public vault links
    const publicVaults = vaults.filter((v) => v.visibility === 'public');
    if (publicVaults.length > 0) {
      groups.push({
        items: publicVaults.map((vault) => ({
          label: vault.title,
          href: vault.href,
        })),
      });
    }

    // Private/Protected/Org vault links
    const privateVaults = vaults.filter((v) => v.visibility !== 'public');
    if (privateVaults.length > 0) {
      groups.push({
        title: 'Private',
        items: privateVaults.map((vault) => ({
          label: vault.title,
          href: vault.href,
        })),
      });
    }

    return groups;
  }, [showAnchors, isLandingPage, isPlatformOrg, vaults]);

  // Two-step scroll behavior:
  // Step 1: headerHidden - menu hides (moves to left pane), breadcrumb appears
  // Step 2: actionBarHidden - action bar appears in nav center
  const headerHidden = navScroll?.headerHidden;
  const hideMenu = headerHidden;
  const showActionBar = navScroll?.actionBarHidden && navScroll?.actionBarState;

  // Inject nav menu into sidebar when:
  // 1. Below wide breakpoint (responsive), OR
  // 2. Header is hidden (scroll-based Step 1)
  const setNavMenu = panelContext?.setNavMenu;
  const leftPanel = panelContext?.leftPanel;
  const shouldInjectMenu = isBelowWide || headerHidden;

  useLayoutEffect(() => {
    if (!setNavMenu) return;

    if (shouldInjectMenu && navMenuGroups.length > 0) {
      // When leftPanel exists (e.g., vault menu), collapse nav menu by default
      const defaultOpen = !leftPanel;
      setNavMenu(<NavMenuSection groups={navMenuGroups} defaultOpen={defaultOpen} />);
    } else {
      setNavMenu(null);
    }

    return () => {
      setNavMenu(null);
    };
  }, [shouldInjectMenu, navMenuGroups, setNavMenu, leftPanel]);

  // Prevent hydration mismatch by rendering nothing until mounted on client
  if (!mounted) {
    return <div className='hidden md:flex items-center gap-1 min-h-[40px]' />;
  }

  return (
    <div className='nav-center-content relative flex items-center justify-center w-full'>
      {/* VIEW 1: Normal Navigation (Features, How it works, Vaults) */}
      {/* Shows when NOT scrolled past header (Step 1) AND when above nav-wide breakpoint */}
      {/* Hides when headerHidden - menu moves to left pane */}
      <div
        className={cn(
          'hidden items-center gap-1 transition-all duration-300 ease-out',
          // Only show on nav-wide screens (≥1200px) - on smaller screens, menu is in sidebar
          'nav-wide:flex',
          hideMenu
            ? 'opacity-0 -translate-y-4 pointer-events-none absolute'
            : 'opacity-100 translate-y-0'
        )}>
        {/* Page Section Anchors (only for platform organization) */}
        {showAnchors &&
          PAGE_SECTIONS.map((section) => {
            // On landing page, use hash-only; elsewhere, link to /welcome#anchor
            const href = isLandingPage ? `#${section.anchor}` : `/welcome#${section.anchor}`;
            // Check if anchor is active - highlight when on landing page AND hash matches
            // Special case: if no hash and section is 'welcome', highlight it as default
            const currentHash = activeHash || (pathname === '/welcome' ? '#welcome' : '');
            const isActive = isLandingPage && currentHash === `#${section.anchor}`;

            return (
              <Link
                key={section.anchor}
                href={href}
                onClick={() => setActiveHash(`#${section.anchor}`)}
                className={cn(
                  'px-4 py-2 text-sm rounded-lg transition-colors',
                  isActive
                    ? // Active: only text changes to emerald, no hover effects
                      'font-medium text-emerald-600 dark:text-emerald-400'
                    : // Inactive: normal hover effects
                      'text-muted-foreground hover:text-foreground hover:bg-muted/5'
                )}>
                {section.label}
              </Link>
            );
          })}

        {/* Showcase Dropdown (only for platform organization owner) */}
        {isPlatformOrg && (
          <DropdownMenu>
            <DropdownMenuTrigger
              className={cn(
                'px-4 py-2 text-sm rounded-lg transition-colors outline-none',
                pathname.startsWith('/showcase')
                  ? 'font-medium text-emerald-600 dark:text-emerald-400'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/5'
              )}>
              Showcase
            </DropdownMenuTrigger>
            <DropdownMenuContent align='start' sideOffset={8}>
              {SHOWCASE_ITEMS.map((item) => (
                <DropdownMenuItem key={item.href} asChild>
                  <Link href={item.href}>{item.label}</Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/* Public Vault Links */}
        {!loading &&
          vaults
            .filter((vault) => vault.visibility === 'public')
            .map((vault) => {
              const isActive = pathname.startsWith(vault.href);
              return (
                <Link
                  key={vault.id}
                  href={vault.href}
                  className={cn(
                    'px-4 py-2 text-sm rounded-lg transition-colors',
                    isActive
                      ? 'font-medium text-emerald-600 dark:text-emerald-400'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/5'
                  )}>
                  {vault.title}
                </Link>
              );
            })}

        {/* Divider between public section (Welcome/Showcase/Public Vaults) and private vaults */}
        {/* Only show for platform org, and only if there are private vaults */}
        {isPlatformOrg && vaults.some((v) => v.visibility !== 'public') && (
          <div className='h-6 w-px mx-2 bg-border' />
        )}

        {/* Private/Protected/Org Vault Links */}
        {!loading &&
          vaults
            .filter((vault) => vault.visibility !== 'public')
            .map((vault) => {
              const isActive = pathname.startsWith(vault.href);
              return (
                <Link
                  key={vault.id}
                  href={vault.href}
                  className={cn(
                    'px-4 py-2 text-sm rounded-lg transition-colors',
                    isActive
                      ? 'font-medium text-emerald-600 dark:text-emerald-400'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/5'
                  )}>
                  {vault.title}
                </Link>
              );
            })}
      </div>

      {/* VIEW 2: ActionBar (scrolled state in document view) */}
      {/* Shows when in vault AND scrolled past action bar - on all screen sizes */}
      <div
        className={cn(
          'flex items-center w-full transition-all duration-300 ease-out',
          showActionBar
            ? 'opacity-100 translate-y-0'
            : 'opacity-0 translate-y-4 pointer-events-none absolute'
        )}>
        {navScroll?.actionBarState && <NavActionBarContent />}
      </div>
    </div>
  );
}

/**
 * NavActionBarContent - The actual action bar content rendered in center zone
 * Separate groups but aligned to the right (no space between left/right sides)
 */
function NavActionBarContent() {
  const navScroll = useNavScrollOptional();
  const [workflowDropdownOpen, setWorkflowDropdownOpen] = useState(false);
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
  const exportDropdownRef = useRef<HTMLDivElement>(null);
  const { meta } = useHeadings();
  const isStatisticsPage = (meta as any)?.isStatisticsPage === true;

  useEffect(() => {
    if (!exportDropdownOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setExportDropdownOpen(false);
    };
    const handleClickOutside = (e: MouseEvent) => {
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(e.target as Node)) {
        setExportDropdownOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [exportDropdownOpen]);

  if (!navScroll?.actionBarState) return null;

  const {
    isEditMode,
    hasDraft,
    isViewingDraft,
    draftStatus,
    canEdit,
    canComment,
    isSaveDisabled,
    isSaving,
    rightPanelMode,
    copiedMarkdown,
    copiedAgentReference,
    isDownloadingPdf,
    isDownloadingDocx,
  } = navScroll.actionBarState;
  const callbacks = navScroll.actionBarCallbacksRef.current;

  const handlePanelToggle = (panel: 'outline' | 'comments') => {
    callbacks.onRightPanelModeChange?.(panel);
  };

  const handleWorkflowAction = (action: WorkflowAction) => {
    setWorkflowDropdownOpen(false);
    if (action === 'publish') {
      callbacks.onPublish?.();
    } else if (action === 'draft') {
      callbacks.onStatusChange?.('draft');
    } else if (action === 'mark_for_review') {
      callbacks.onStatusChange?.('marked_for_review');
    } else if (action === 'validate') {
      callbacks.onStatusChange?.('validated');
    }
  };

  const primaryAction = getPrimaryAction(draftStatus);
  const primaryActionInfo = workflowActions.find((a) => a.id === primaryAction)!;
  const secondaryActions = getSecondaryActions(draftStatus);
  const saveDisabled = isSaveDisabled || isSaving;
  const saveTooltipLabel = isSaving ? 'Saving...' : saveDisabled ? 'No changes to save' : 'Save draft';
  const isExportBusy = isDownloadingPdf || isDownloadingDocx;
  const hasExportOptions = Boolean(callbacks.onDownloadPdf || callbacks.onDownloadDocx);

  return (
    <div className='nav-action-bar-content flex items-center justify-end w-full gap-2'>
      {/* Navigation group: Previous, Next, Refresh */}
      <div className={styles.group}>
        <ActionBarTooltip label='Previous page'>
          <button type='button' className={styles.btn} onClick={callbacks.onBack}>
            <LuArrowLeft className='h-4 w-4' />
          </button>
        </ActionBarTooltip>
        <ActionBarTooltip label='Next page'>
          <button type='button' className={cn(styles.btn, styles.borderL)} onClick={callbacks.onForward}>
            <LuArrowRight className='h-4 w-4' />
          </button>
        </ActionBarTooltip>
        <ActionBarTooltip label='Refresh'>
          <button type='button' className={cn(styles.btn, styles.borderL)} onClick={callbacks.onRefresh}>
            <LuRefreshCw className='h-4 w-4' />
          </button>
        </ActionBarTooltip>
      </div>

      {/* Edit controls - only show if user can edit */}
      {canEdit && (
        <>
          {isEditMode ? (
            <div className={styles.group}>
              <ActionBarTooltip label='Undo'>
                <button type='button' className={styles.btn} onClick={callbacks.onUndo}>
                  <LuUndo2 className='h-4 w-4' />
                </button>
              </ActionBarTooltip>
              <ActionBarTooltip label='Redo'>
                <button type='button' className={cn(styles.btn, styles.borderL)} onClick={callbacks.onRedo}>
                  <LuRedo2 className='h-4 w-4' />
                </button>
              </ActionBarTooltip>
              <ActionBarTooltip label={saveTooltipLabel}>
                <button
                  type='button'
                  className={cn(styles.btn, styles.borderL)}
                  onClick={callbacks.onSave}
                  disabled={saveDisabled}>
                  <LuSave className='h-4 w-4' />
                </button>
              </ActionBarTooltip>
              <ActionBarTooltip label='Cancel'>
                <button
                  type='button'
                  className={cn(styles.btn, styles.borderL)}
                  onClick={() => callbacks.onEditModeChange?.(false)}>
                  <LuX className='h-4 w-4' />
                </button>
              </ActionBarTooltip>
            </div>
          ) : (
            <ActionBarTooltip label='Edit'>
              <button
                type='button'
                className={cn(styles.group, styles.btn, 'rounded-lg')}
                onClick={() => callbacks.onEditModeChange?.(true)}>
                <LuPenLine className='h-4 w-4' />
              </button>
            </ActionBarTooltip>
          )}

          {/* Workflow split button - only available when viewing a draft */}
          {hasDraft && isViewingDraft && (
            <div className='relative'>
              <div className={styles.group}>
                {/* Primary action button */}
                <ActionBarTooltip label={primaryActionInfo.label}>
                  <button
                    type='button'
                    className={cn(styles.btn, 'w-auto px-2 gap-1', primaryActionInfo.colorClass)}
                    onClick={() => handleWorkflowAction(primaryAction)}>
                    {getWorkflowIcon(primaryAction, primaryActionInfo.colorClass)}
                    <span className='text-xs'>{primaryActionInfo.label}</span>
                  </button>
                </ActionBarTooltip>
                {/* Dropdown toggle */}
                <ActionBarTooltip label='More actions'>
                  <button
                    type='button'
                    className={cn(styles.btn, styles.borderL, 'w-6')}
                    onClick={() => setWorkflowDropdownOpen(!workflowDropdownOpen)}>
                    <LuChevronDown className={cn('h-3 w-3 transition-transform', workflowDropdownOpen && 'rotate-180')} />
                  </button>
                </ActionBarTooltip>
              </div>

              {/* Dropdown menu */}
              {workflowDropdownOpen && (
                <div className='absolute top-full right-0 mt-1 py-1 w-36 rounded-lg border shadow-xl z-50 bg-popover border-border'>
                  <div className='px-3 py-1.5 text-[10px] uppercase tracking-wider text-muted-foreground'>
                    Other Actions
                  </div>
                  {secondaryActions.map((actionId) => {
                    const action = workflowActions.find((a) => a.id === actionId)!;
                    return (
                      <button
                        key={actionId}
                        type='button'
                        onClick={() => handleWorkflowAction(actionId)}
                        className='w-full px-3 py-1.5 text-left text-sm transition-colors flex items-center gap-2 hover:bg-muted text-foreground'>
                        {getWorkflowIcon(actionId, action.colorClass)}
                        {action.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Export group: Copy markdown, Agent, Download (dropdown) */}
      <div className='relative' ref={exportDropdownRef}>
        <div className={styles.group}>
          <ActionBarTooltip label={copiedMarkdown ? 'Copied' : 'Copy source'}>
            <button
              type='button'
              className={cn(styles.btn, copiedMarkdown && styles.btnCopied)}
              onClick={callbacks.onCopyMarkdown}>
              {copiedMarkdown ? <LuCheck className='h-4 w-4' /> : <LuCopy className='h-4 w-4' />}
            </button>
          </ActionBarTooltip>

          <ActionBarTooltip label={copiedAgentReference ? 'Copied' : 'Copy agent reference'}>
            <button
              type='button'
              className={cn(styles.btn, styles.borderL, copiedAgentReference && styles.btnCopied)}
              onClick={callbacks.onCopyAgentReference}>
              {copiedAgentReference ? <LuCheck className='h-4 w-4' /> : <LuBot className='h-4 w-4' />}
            </button>
          </ActionBarTooltip>

          {hasExportOptions && (
            <ActionBarTooltip label={isExportBusy ? 'Please wait...' : 'Download'}>
              <button
                type='button'
                className={cn(styles.btn, styles.borderL, 'w-auto px-2 gap-1')}
                onClick={() => setExportDropdownOpen(!exportDropdownOpen)}
                disabled={isExportBusy}>
                {isDownloadingPdf || isDownloadingDocx ? (
                  <LuLoader className='h-4 w-4 animate-spin' />
                ) : (
                  <LuDownload className='h-4 w-4' />
                )}
                <LuChevronDown className={cn('h-3 w-3 transition-transform', exportDropdownOpen && 'rotate-180')} />
              </button>
            </ActionBarTooltip>
          )}
        </div>

        {exportDropdownOpen && hasExportOptions && (
          <div className='absolute top-full right-0 mt-1 py-1 w-44 rounded-lg border shadow-xl z-50 bg-popover border-border'>
            {callbacks.onDownloadPdf && (
              <button
                type='button'
                onClick={() => {
                  setExportDropdownOpen(false);
                  callbacks.onDownloadPdf?.();
                }}
                disabled={isExportBusy}
                className='w-full px-3 py-1.5 text-left text-sm transition-colors flex items-center gap-2 hover:bg-muted text-foreground disabled:opacity-50 disabled:pointer-events-none'>
                {isDownloadingPdf ? (
                  <LuLoader className='h-4 w-4 animate-spin' />
                ) : (
                  <LuDownload className='h-4 w-4' />
                )}
                {isDownloadingPdf ? 'Generating PDF...' : 'Download PDF'}
              </button>
            )}

            {callbacks.onDownloadDocx && (
              <button
                type='button'
                onClick={() => {
                  setExportDropdownOpen(false);
                  callbacks.onDownloadDocx?.();
                }}
                disabled={isExportBusy}
                className='w-full px-3 py-1.5 text-left text-sm transition-colors flex items-center gap-2 hover:bg-muted text-foreground disabled:opacity-50 disabled:pointer-events-none'>
                {isDownloadingDocx ? (
                  <LuLoader className='h-4 w-4 animate-spin' />
                ) : (
                  <LuFileText className='h-4 w-4' />
                )}
                {isDownloadingDocx ? 'Generating Word...' : 'Download Word'}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Panel toggle group: Outline, Comments */}
      {!isStatisticsPage && (
        <div className={styles.group}>
          <ActionBarTooltip label='Toggle outline'>
            <button
              type='button'
              className={cn(styles.btn, rightPanelMode === 'outline' && styles.btnActive)}
              onClick={() => handlePanelToggle('outline')}>
              <LuList className='h-4 w-4' />
            </button>
          </ActionBarTooltip>
          {canComment && (
            <ActionBarTooltip label='Toggle comments'>
              <button
                type='button'
                className={cn(styles.btn, styles.borderL, rightPanelMode === 'comments' && styles.btnActive)}
                onClick={() => handlePanelToggle('comments')}>
                <LuMessageSquare className='h-4 w-4' />
              </button>
            </ActionBarTooltip>
          )}
        </div>
      )}
    </div>
  );
}

export default NavCenterContent;
